import { Box, Stack } from '@mui/material';
import React, { useState } from 'react';
import * as Yup from 'yup';
import { requirementsValidationSchema } from 'modules/analyse/constants/requirementsValidationSchema';
import { actionsValidationSchema } from 'modules/analyse/constants/actionsValidationsSchema';
import { useFormik } from 'formik';
import { AnalyseTemplateFormValues } from 'modules/analyse-templates/types/AnalyseTemplateFormValues';
import { FormField } from 'common/ui';
import { Button } from 'system/atoms';
import { AutomationComponents } from 'common/ui/AutomationComponents';
import { Checkbox } from 'common/form';
import { useIsAdmin } from 'hooks/useIsAdmin';

type Props = {
  onFormSubmitted: (values: AnalyseTemplateFormValues) => void;
  onCanceled: () => void;
  initialValues?: AnalyseTemplateFormValues;
  isVerticalForm?: boolean;
  submitButtonLabel?: string;
  autofillDefaultTemplate?: boolean;
};

const analyseTemplateForm = Yup.object().shape({
  templateName: Yup.string().required(),
  analysisRequirements:
    requirementsValidationSchema.fields.analysisRequirements,
  actionAutomationTasks: actionsValidationSchema(true).fields.automationTasks
});

export const AnalyseTemplateForm: React.FC<Props> = ({
  onFormSubmitted,
  onCanceled,
  initialValues,
  isVerticalForm,
  autofillDefaultTemplate = false,
  submitButtonLabel = 'Save'
}) => {
  const { isAdmin } = useIsAdmin();

  const [enableReinitialize, setEnableReinitialize] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formik = useFormik<AnalyseTemplateFormValues>({
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: initialValues || {
      templateName: '',
      ruleAutomationTasks: [],
      actionAutomationTasks: [],
      setAsDefault: false
    },
    validationSchema: analyseTemplateForm,
    onSubmit: () => {
      // do nothing
    },
    enableReinitialize: true
  });

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await formik.setTouched({
        templateName: true
      });

      const errors = await formik.validateForm();
      if (Object.keys(errors).length === 0 || !errors) {
        setEnableReinitialize(false);
        await onFormSubmitted(formik.values);
      } else {
        formik.setErrors(errors);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      <FormField
        isVertical={isVerticalForm}
        titleSx={isVerticalForm ? { fontSize: '20px' } : undefined}
        width={isVerticalForm ? '100%' : undefined}
        title="Details"
        gap={isVerticalForm ? '40px' : undefined}
        subTitle="Help others to identify and use this template correctly"
        textFields={[
          {
            label: 'Template name',
            props: {
              id: 'templateName',
              name: 'templateName',
              value: formik.values.templateName,
              onChange: (event) => {
                formik.validateField('templateName');
                formik.handleChange(event);
              },
              error:
                formik.touched.templateName && !!formik.errors.templateName,
              errorText: 'Enter template name',
              sx: {
                maxWidth: isVerticalForm ? '100%' : '50%'
              }
            }
          }
        ]}
      />
      <AutomationComponents
        isVerticalForm={isVerticalForm}
        titleSx={isVerticalForm ? { fontSize: '20px' } : undefined}
        formik={formik}
        actionsEnableReinitialize={enableReinitialize}
        autofillDefaultTemplate={autofillDefaultTemplate}
        include={{ rules: true, actions: true }}
        gap={isVerticalForm ? '20px' : undefined}
      />
      <FormField
        isVertical={isVerticalForm}
        gap={isVerticalForm ? '0px' : undefined}
        customComponents={[
          <Stack direction="column" gap="40px">
            {isAdmin && (
              <Checkbox
                field={{
                  checkboxLabel: 'Set as default template for all new analyses',
                  containerSx: {
                    m: 0,
                    ml: 0.2 // align checkbox
                  }
                }}
                value={formik.values.setAsDefault}
                onChange={(value) => {
                  formik.setFieldValue('setAsDefault', value);
                }}
              />
            )}

            <Stack direction="row" gap={1}>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={isSubmitting}
                loading={isSubmitting}
              >
                {submitButtonLabel}
              </Button>

              <Button onClick={onCanceled}>Cancel</Button>
            </Stack>
          </Stack>
        ]}
      />
    </Box>
  );
};
